import type { CSSProperties } from "react";

export const TOOLTIP_MAX_WIDTH = 340;
export const TOOLTIP_OFFSET = 12;

type TooltipStyleName =
  | "position"
  | "max-width"
  | "padding"
  | "background-color"
  | "border"
  | "border-radius"
  | "box-shadow"
  | "color"
  | "font-family"
  | "font-size"
  | "line-height"
  | "text-align"
  | "white-space"
  | "pointer-events"
  | "z-index";

export const TOOLTIP_STYLE: Record<TooltipStyleName, string> = {
  position: "absolute",
  "max-width": `${TOOLTIP_MAX_WIDTH}px`,
  padding: "10px 12px",
  "background-color": "#ffffff",
  border: "1px solid #d5d9e0",
  "border-radius": "8px",
  "box-shadow": "0 2px 8px rgba(5, 0, 78, 0.15)",
  color: "#1f2937",
  "font-family": "inherit",
  "font-size": "13px",
  "line-height": "1.45",
  "text-align": "left",
  "white-space": "normal",
  "pointer-events": "none",
  "z-index": "1500",
};

/** Same styling for tooltips rendered as React elements rather than by d3. */
export const TOOLTIP_STYLE_REACT: CSSProperties = {
  position: "absolute",
  maxWidth: TOOLTIP_MAX_WIDTH,
  padding: "10px 12px",
  backgroundColor: "#ffffff",
  border: "1px solid #d5d9e0",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(5, 0, 78, 0.15)",
  color: "#1f2937",
  fontFamily: "inherit",
  fontSize: "13px",
  lineHeight: 1.45,
  textAlign: "left",
  whiteSpace: "normal",
  pointerEvents: "none",
  zIndex: 1500,
};

const HTML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

/** Series labels come from user-entered data, so escape before using .html(). */
export function escapeHtml(value: unknown): string {
  return String(value ?? "").replace(
    /[&<>"']/g,
    (character) => HTML_ESCAPES[character]
  );
}

type StyleableSelection = { style: (name: string, value: string) => unknown };

export function applyTooltipStyle<T extends StyleableSelection>(
  selection: T
): T {
  Object.entries(TOOLTIP_STYLE).forEach(([name, value]) => {
    selection.style(name, value);
  });
  return selection;
}

/**
 * Places the tooltip near the pointer, flipping to the opposite side when it
 * would run past the viewport edge. Without this, a shrink-to-fit tooltip near
 * the right edge is squeezed into a narrow, barely readable column.
 */
export function positionTooltip(
  node: HTMLElement | null,
  event: { pageX: number; pageY: number }
): void {
  if (!node) return;

  const tooltipWidth = node.offsetWidth || TOOLTIP_MAX_WIDTH;
  const tooltipHeight = node.offsetHeight || 0;
  const viewportWidth = document.documentElement.clientWidth;
  const viewportHeight = document.documentElement.clientHeight;
  const scrollX = window.scrollX || 0;
  const scrollY = window.scrollY || 0;

  let left = event.pageX + TOOLTIP_OFFSET;
  if (left + tooltipWidth > scrollX + viewportWidth) {
    left = event.pageX - tooltipWidth - TOOLTIP_OFFSET;
  }
  left = Math.max(scrollX + 8, left);

  let top = event.pageY + TOOLTIP_OFFSET;
  if (top + tooltipHeight > scrollY + viewportHeight) {
    top = event.pageY - tooltipHeight - TOOLTIP_OFFSET;
  }
  top = Math.max(scrollY + 8, top);

  node.style.left = `${left}px`;
  node.style.top = `${top}px`;
}
